import { FC, useState } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import HelpTooltip from '@/components/ui/help-tooltip';
import { cn } from '@/lib/utils';
import { CollectionSource } from '@/types/source-types';
import { Plus } from 'lucide-react';
import { WordPressSiteCard } from './wordpress-site-card';
import { createEmptySiteState } from './wordpress-source-utils';
import type {
  WordPressSiteFormState,
  WordPressSiteFormErrors,
} from './wordpress-site-form';

// Re-export for backward compatibility
export type { WordPressSiteFormState, WordPressSiteFormErrors };

// Legacy type aliases for consumers still using the old names
export type WordPressSourceFormState = WordPressSiteFormState;
export type WordPressSourceFormErrors = WordPressSiteFormErrors;

interface CollectionSourceSectionProps {
  disabled?: boolean;
  source: CollectionSource;
  onSourceChange: (next: CollectionSource) => void;
  wordpressSites: WordPressSiteFormState[];
  onWordPressSitesChange: (sites: WordPressSiteFormState[]) => void;
  siteErrors: Record<string, WordPressSiteFormErrors>;
  reconcileEnabled: boolean;
  onReconcileEnabledChange: (next: boolean) => void;
  isEditing?: boolean;
  expandedSiteId?: string | null;
  onExpandedSiteIdChange?: (siteId: string | null) => void;
  /**
   * Hide per-site management (site cards, add-site, reconcile toggle). Used by
   * the edit modal when a collection already has WordPress sites — those are
   * managed inline on the collection page instead. The source radio still
   * renders. Defaults true (create flow + uploads→wp conversion need it).
   */
  showSiteManagement?: boolean;
  /** Disable the source radios (e.g. a collection with WP sites already configured). */
  disableSourceChange?: boolean;
  /** Help text shown under the radios when source change is disabled. */
  sourceChangeDisabledReason?: string;
}

const SOURCE_OPTIONS: Array<{ value: CollectionSource; label: string }> = [
  { value: 'uploads', label: 'Uploads' },
  { value: 'wordpress_api', label: 'WordPress API' },
];

export const CollectionSourceSection: FC<CollectionSourceSectionProps> = ({
  disabled = false,
  source,
  onSourceChange,
  wordpressSites,
  onWordPressSitesChange,
  siteErrors,
  reconcileEnabled,
  onReconcileEnabledChange,
  isEditing = false,
  expandedSiteId: controlledExpandedSiteId,
  onExpandedSiteIdChange,
  showSiteManagement = true,
  disableSourceChange = false,
  sourceChangeDisabledReason,
}) => {
  const showWordPressFields = source === 'wordpress_api';

  // Internal expanded state (used if not controlled from parent)
  const [internalExpandedSiteId, setInternalExpandedSiteId] = useState<
    string | null
  >(wordpressSites.length > 0 ? wordpressSites[0].siteId : null);

  const expandedSiteId =
    controlledExpandedSiteId !== undefined
      ? controlledExpandedSiteId
      : internalExpandedSiteId;

  const setExpandedSiteId = (siteId: string | null) => {
    if (onExpandedSiteIdChange) {
      onExpandedSiteIdChange(siteId);
    } else {
      setInternalExpandedSiteId(siteId);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Label className='text-sm font-medium'>Collection Source</Label>
          <HelpTooltip label='Collection source help'>
            WordPress collections are indexed automatically on a schedule.
          </HelpTooltip>
        </div>
        <RadioGroup
          value={source}
          onValueChange={(value) => onSourceChange(value as CollectionSource)}
          className='flex flex-wrap gap-3'
          disabled={disabled || disableSourceChange}
        >
          {SOURCE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer transition-colors',
                source === option.value && 'border-primary',
                (disabled || disableSourceChange) &&
                  'cursor-not-allowed opacity-70',
              )}
            >
              <RadioGroupItem
                value={option.value}
                id={`collection-source-${option.value}`}
                disabled={disabled || disableSourceChange}
              />
              <span className='text-sm font-medium'>{option.label}</span>
            </label>
          ))}
        </RadioGroup>
        {disableSourceChange && sourceChangeDisabledReason && (
          <p className='text-xs text-muted-foreground'>
            {sourceChangeDisabledReason}
          </p>
        )}
      </div>

      {showWordPressFields && showSiteManagement && (
        <div className='space-y-3'>
          {wordpressSites.map((site, index) => (
            <WordPressSiteCard
              key={site.siteId}
              site={site}
              isOpen={expandedSiteId === site.siteId}
              onToggle={() =>
                setExpandedSiteId(
                  expandedSiteId === site.siteId ? null : site.siteId,
                )
              }
              onChange={(patch) => {
                const updated = [...wordpressSites];
                updated[index] = { ...updated[index], ...patch };
                onWordPressSitesChange(updated);
              }}
              onRemove={() => {
                const remaining = wordpressSites.filter((_, i) => i !== index);
                onWordPressSitesChange(remaining);
                // If we removed the expanded site, expand the first remaining one
                if (expandedSiteId === site.siteId && remaining.length > 0) {
                  setExpandedSiteId(remaining[0].siteId);
                }
              }}
              onDuplicate={() => {
                const copy: WordPressSiteFormState = {
                  ...site,
                  siteId: crypto.randomUUID().slice(0, 12),
                  label: `${site.label || site.baseUrl || 'Site'} (copy)`,
                  // Reset password-related state for the copy
                  password: '',
                  isPasswordDirty: false,
                  hasCredentials: false,
                };
                onWordPressSitesChange([...wordpressSites, copy]);
                setExpandedSiteId(copy.siteId);
              }}
              isOnlySite={wordpressSites.length === 1}
              errors={siteErrors[site.siteId] || {}}
              hasErrors={Object.keys(siteErrors[site.siteId] || {}).length > 0}
              disabled={disabled}
              isEditing={isEditing}
            />
          ))}
          <Button
            type='button'
            variant='outline'
            className='w-full border-dashed'
            onClick={() => {
              const newSite = createEmptySiteState();
              onWordPressSitesChange([...wordpressSites, newSite]);
              setExpandedSiteId(newSite.siteId);
            }}
            disabled={disabled}
          >
            <Plus className='size-4 mr-2' />
            Add another WordPress site
          </Button>

          <label
            className={cn(
              'flex items-start gap-2 rounded-md border border-border px-3 py-2',
              disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
            )}
          >
            <Checkbox
              checked={reconcileEnabled}
              onCheckedChange={(checked) =>
                onReconcileEnabledChange(checked === true)
              }
              disabled={disabled}
              className='mt-0.5'
            />
            <span className='space-y-0.5'>
              <span className='flex items-center gap-2 text-sm font-medium'>
                Enable nightly deletion reconcile
                <HelpTooltip label='Nightly reconcile help'>
                  When on, a nightly job removes indexed content for posts
                  deleted or unpublished in WordPress. Off by default.
                </HelpTooltip>
              </span>
              <span className='block text-xs text-muted-foreground'>
                Sweeps this collection against live WordPress content each night
                and deletes points for removed posts.
              </span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
